import React, { useEffect, useRef, useMemo, useState } from 'react';
import { Box, useTheme, IconButton, Tooltip } from '@mui/material';
import { MarketMetadata, MarketAnalysisData, SeriesVisibility, VolumeVisibility } from './types';
import { aggregateTradesByPeriod } from './volumeUtils';
import { useChartLayout, useChartScales, useParsedData, useChartRender, ANALYSIS_COLORS, ChartPoint } from './chart';

interface OddsPerformanceChartProps {
    data: ChartPoint[];
    markets: MarketMetadata[];
    marketColors: string[];
    eventTitle: string;
    useDecimalScale?: boolean;
    endDate?: Date;
    extendToEventEnd?: boolean;
    onExtendModeChange?: (extend: boolean) => void;
    onDateRangeChange?: (startDate: Date, endDate: Date) => void;
    onResetZoom?: () => void;
    analysisData?: MarketAnalysisData | null;
    seriesVisibility?: SeriesVisibility;
    isFullSeriesMode?: boolean;
    showVolumePanel?: boolean;
    volumeVisibility?: VolumeVisibility;
    candleFrequencyMs?: number;
}

export const OddsPerformanceChart: React.FC<OddsPerformanceChartProps> = ({
    data,
    markets,
    marketColors,
    eventTitle,
    useDecimalScale = false,
    endDate,
    extendToEventEnd = false,
    onExtendModeChange,
    onDateRangeChange,
    onResetZoom,
    analysisData,
    seriesVisibility,
    isFullSeriesMode = false,
    showVolumePanel = false,
    volumeVisibility = { showVolume: false, metric: 'volume' },
    candleFrequencyMs,
}) => {
    const theme = useTheme();
    const svgRef = useRef<SVGSVGElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [width, setWidth] = useState(800);
    const [isLogScale, setIsLogScale] = useState(false);

    // Aggregate trades for buy/sell volume breakdown
    const aggregatedVolume = useMemo(() => {
        if (!analysisData?.candles || !analysisData?.trades) return [];
        return aggregateTradesByPeriod(analysisData.trades, analysisData.candles, candleFrequencyMs);
    }, [analysisData?.candles, analysisData?.trades, candleFrequencyMs]);

    // Handle responsive width
    useEffect(() => {
        if (!containerRef.current) return;
        const observer = new ResizeObserver(entries => {
            for (let entry of entries) {
                setWidth(entry.contentRect.width);
            }
        });
        observer.observe(containerRef.current);
        return () => observer.disconnect();
    }, []);

    // Parse timestamps
    const parsedData = useParsedData(data);

    // Calculate layout
    const layout = useChartLayout({
        width,
        showVolumePanel,
        volumeVisibility,
        analysisData,
    });

    // Calculate scales
    const scales = useChartScales({
        parsedData,
        analysisData,
        markets,
        layout,
        isFullSeriesMode,
        isLogScale,
        useDecimalScale,
        endDate,
        aggregatedVolume,
        volumeVisibility,
    });

    // Render the chart
    useChartRender({
        svgRef,
        scales,
        layout,
        theme,
        parsedData,
        analysisData,
        aggregatedVolume,
        isFullSeriesMode,
        seriesVisibility,
        volumeVisibility,
        useDecimalScale,
        isLogScale,
        markets,
        marketColors,
        onDateRangeChange,
        candleFrequencyMs,
    });

    return (
        <Box ref={containerRef} sx={{ width: '100%', overflowX: 'hidden', position: 'relative' }}>
            <Tooltip title={`Switch to ${isLogScale ? 'linear' : 'logarithmic'} scale`}>
                <IconButton
                    onClick={() => setIsLogScale(!isLogScale)}
                    sx={{
                        position: 'absolute',
                        bottom: 10,
                        right: 10,
                        zIndex: 1,
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        color: theme.palette.text.primary,
                        width: 28,
                        height: 28,
                        fontSize: '12px',
                        '&:hover': { backgroundColor: 'rgba(0,0,0,0.2)' }
                    }}
                >
                    {isLogScale ? 'LIN' : 'LOG'}
                </IconButton>
            </Tooltip>

            {onResetZoom && (
                <Tooltip title="Reset zoom">
                    <IconButton
                        onClick={onResetZoom}
                        sx={{
                            position: 'absolute',
                            bottom: 10,
                            right: 80,
                            zIndex: 1,
                            backgroundColor: 'rgba(0,0,0,0.1)',
                            color: theme.palette.text.primary,
                            width: 28,
                            height: 28,
                            fontSize: '26px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.2)' }
                        }}
                    >
                        ⟲
                    </IconButton>
                </Tooltip>
            )}

            {onExtendModeChange && (
                <Tooltip title={extendToEventEnd ? "Show only data range" : "Extend to event end"}>
                    <IconButton
                        onClick={() => onExtendModeChange(!extendToEventEnd)}
                        sx={{
                            position: 'absolute',
                            bottom: 10,
                            right: 45,
                            zIndex: 1,
                            backgroundColor: 'rgba(0,0,0,0.1)',
                            color: theme.palette.text.primary,
                            width: 28,
                            height: 28,
                            fontSize: '18px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            '&:hover': { backgroundColor: 'rgba(0,0,0,0.2)' }
                        }}
                    >
                        {extendToEventEnd ? '⇔' : '→'}
                    </IconButton>
                </Tooltip>
            )}

            <svg
                ref={svgRef}
                width={width}
                height={layout.svgTotalHeight}
                style={{ background: 'transparent' }}
            />

            <ChartLegend
                isFullSeriesMode={isFullSeriesMode}
                seriesVisibility={seriesVisibility}
                markets={markets}
                marketColors={marketColors}
            />
        </Box>
    );
};

// Legend component
interface ChartLegendProps {
    isFullSeriesMode: boolean;
    seriesVisibility?: SeriesVisibility;
    markets: MarketMetadata[];
    marketColors: string[];
}

const ChartLegend: React.FC<ChartLegendProps> = ({
    isFullSeriesMode,
    seriesVisibility,
    markets,
    marketColors,
}) => (
    <Box sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 2,
        justifyContent: 'center',
        mt: 1,
        fontSize: '12px'
    }}>
        {isFullSeriesMode ? (
            <>
                {seriesVisibility?.bidLine !== false && (
                    <LegendItem color={ANALYSIS_COLORS.bid} label="Bid" type="line" />
                )}
                {seriesVisibility?.askLine !== false && (
                    <LegendItem color={ANALYSIS_COLORS.ask} label="Ask" type="line" />
                )}
                {seriesVisibility?.fairValueLine !== false && (
                    <LegendItem color={ANALYSIS_COLORS.fairValue} label="Target Probability" type="line" />
                )}
                {seriesVisibility?.trades !== false && (
                    <>
                        <LegendItem color={ANALYSIS_COLORS.buyTrade} label="Buy" type="triangle-up" />
                        <LegendItem color={ANALYSIS_COLORS.sellTrade} label="Sell" type="triangle-down" />
                    </>
                )}
            </>
        ) : (
            markets.map((market, index) => (
                <LegendItem
                    key={market.id}
                    color={marketColors[index]}
                    label={market.name}
                    type="line"
                />
            ))
        )}
    </Box>
);

interface LegendItemProps {
    color: string;
    label: string;
    type: 'line' | 'triangle-up' | 'triangle-down';
}

const LegendItem: React.FC<LegendItemProps> = ({ color, label, type }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {type === 'line' && (
            <Box sx={{ width: 20, height: 2, backgroundColor: color }} />
        )}
        {type === 'triangle-up' && (
            <Box sx={{
                width: 0,
                height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderBottom: `10px solid ${color}`,
            }} />
        )}
        {type === 'triangle-down' && (
            <Box sx={{
                width: 0,
                height: 0,
                borderLeft: '6px solid transparent',
                borderRight: '6px solid transparent',
                borderTop: `10px solid ${color}`,
            }} />
        )}
        <span>{label}</span>
    </Box>
);
